import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { ExposedGetCommentsResponse } from '~/api';
import { exposedGetComments } from '~/api';
import { addComments, selectSortedCommentThreads, selectSortedComments, selectVcmId, useStore } from '~/store';

interface Params<R = InfiniteData<ExposedGetCommentsResponse, string | null>> {
  parentId?: string | null;
  enabled?: boolean;
  select?: (data: InfiniteData<ExposedGetCommentsResponse, string | null>) => R;
}

const EMPTY_PAGE: ExposedGetCommentsResponse = { comments: [] };
const PAGE_SIZE = 20;

export function useGetComments<R = InfiniteData<ExposedGetCommentsResponse, string | null>>(params?: Params<R>) {
  const { parentId = null, enabled, select } = params ?? {};
  const allComment = useStore(selectSortedComments);
  const allCommentThreads = useStore(selectSortedCommentThreads);
  const vcmId = useStore(selectVcmId);
  const query = useInfiniteQuery({
    enabled,
    queryKey: ['comments', parentId, vcmId],
    initialPageParam: null as string | null,
    queryFn: async ctx => {
      const { pageParam: cursor } = ctx;
      const result = await exposedGetComments({
        query: { originId: vcmId, limit: PAGE_SIZE, ...(parentId && { parentId }), ...(cursor && { cursor }) },
      });

      if (result.data?.comments) addComments(result.data.comments);

      return result.data ?? EMPTY_PAGE;
    },

    getNextPageParam: lastPage => {
      if (lastPage.comments.length < PAGE_SIZE) {
        return null;
      }

      return lastPage.cursor ?? null;
    },
    select,
  });

  const filteredComments = allComment.filter(comment => comment.parent_id === parentId);
  const filteredCommentThreads = allCommentThreads.filter(commentThread => commentThread.parent_id === parentId);

  return {
    query,
    comments: filteredComments,
    commentThreads: filteredCommentThreads,
  };
}
